import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import GlassSurface from '../components/effects/GlassSurface';
import Arrow from '../components/ui/Arrow';
import { navigation } from '../content/site';
import {
  getMessages,
  languages,
  localeFromPath,
  localizedPath,
  pathWithoutLocale,
  useMessages,
} from '../i18n/locale';
import Brand from './Brand';
import NavGlassIndicator from './NavGlassIndicator';

export default function SiteHeader({ theme, onThemeToggle }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [overBrightSection, setOverBrightSection] = useState(false);
  const headerRef = useRef(null);
  const [navElement, setNavElement] = useState(null);
  const languageMenuRef = useRef(null);
  const location = useLocation();
  const locale = localeFromPath(location.pathname);
  const currentPage = pathWithoutLocale(location.pathname);
  const messages = useMessages();
  const copy = messages.header;
  useEffect(() => {
    const closeOnOutsideClick = (event) => {
      if (!languageMenuRef.current?.contains(event.target)) languageMenuRef.current.open = false;
    };
    const closeOnEscape = (event) => {
      if (event.key === 'Escape' && languageMenuRef.current) languageMenuRef.current.open = false;
    };
    document.addEventListener('pointerdown', closeOnOutsideClick);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('pointerdown', closeOnOutsideClick);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, []);
  useEffect(() => {
    setMenuOpen(false);
    if (languageMenuRef.current) languageMenuRef.current.open = false;
    if (currentPage !== '/') {
      setOverBrightSection(false);
      return undefined;
    }
    const updateContrast = () => {
      const header = headerRef.current?.getBoundingClientRect();
      const about = document.getElementById('about')?.getBoundingClientRect();
      if (!header || !about) return;
      const centerY = header.top + header.height / 2;
      setOverBrightSection(about.top <= centerY && about.bottom >= centerY);
    };
    updateContrast();
    window.addEventListener('scroll', updateContrast, { passive: true });
    window.addEventListener('resize', updateContrast);
    return () => {
      window.removeEventListener('scroll', updateContrast);
      window.removeEventListener('resize', updateContrast);
    };
  }, [location.pathname, currentPage]);
  const renderLinks = () =>
    navigation.map((item) => (
      <NavLink
        key={item.href}
        to={localizedPath(locale, item.href)}
        end={item.href === '/'}
        className={({ isActive }) => (isActive ? 'site-nav__active' : '')}
        onClick={() => {
          setMenuOpen(false);
          if (item.href === '/') window.scrollTo({ top: 0, behavior: 'smooth' });
        }}
      >
        {messages.navigation[item.key]}
      </NavLink>
    ));
  return (
    <header
      ref={headerRef}
      className={`site-header ${theme === 'light' && overBrightSection ? 'site-header--bright' : ''}`}
    >
      <GlassSurface
        className="navbar-glass"
        width="100%"
        height="100%"
        borderRadius={28}
        borderWidth={0.08}
        brightness={55}
        opacity={0.72}
        blur={10}
        displace={0.4}
        backgroundOpacity={0.08}
        saturation={1}
        distortionScale={-80}
        redOffset={0}
        greenOffset={0}
        blueOffset={0}
        mixBlendMode="normal"
      >
        <div className="nav-shell container">
          <Brand />
          <nav ref={setNavElement} className="site-nav" aria-label={copy.mainNavigation}>
            <NavGlassIndicator navElement={navElement} pathname={location.pathname} />
            {renderLinks()}
          </nav>
          <div className="nav-actions">
            <details className="language-menu" ref={languageMenuRef}>
              <summary aria-label={copy.chooseLanguage}>
                <span aria-hidden="true">◎</span>
                {messages.meta.shortName}
                <span className="language-menu__chevron" aria-hidden="true">
                  ⌄
                </span>
              </summary>
              <div className="language-menu__list">
                {languages.map((language) => (
                  <Link
                    key={language.code}
                    to={`${localizedPath(language.code, currentPage)}${location.search}${location.hash}`}
                    className={locale === language.code ? 'language-menu__active' : ''}
                    aria-current={locale === language.code ? 'true' : undefined}
                    aria-label={getMessages(language.code).meta.name}
                    lang={language.htmlLang}
                    onClick={() => {
                      if (languageMenuRef.current) languageMenuRef.current.open = false;
                    }}
                  >
                    {getMessages(language.code).meta.name}
                    {locale === language.code && <span aria-hidden="true">✓</span>}
                  </Link>
                ))}
              </div>
            </details>
            <button
              className="theme-button"
              type="button"
              onClick={onThemeToggle}
              aria-label={theme === 'dark' ? copy.switchToLight : copy.switchToDark}
              title={theme === 'dark' ? copy.lightMode : copy.darkMode}
            >
              {theme === 'dark' ? '☼' : '☾'}
            </button>
            <Link className="nav-contact" to={localizedPath(locale, '/company/contact')}>
              {copy.contact} <Arrow up />
            </Link>
            <button
              className={`menu-button ${menuOpen ? 'menu-button--open' : ''}`}
              type="button"
              aria-label={menuOpen ? copy.closeMenu : copy.openMenu}
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen(!menuOpen)}
            >
              <span />
              <span />
            </button>
          </div>
        </div>
      </GlassSurface>
      <nav
        className={`mobile-nav ${menuOpen ? 'mobile-nav--open' : ''}`}
        aria-label={copy.mobileNavigation}
      >
        {renderLinks()}
      </nav>
    </header>
  );
}
